import React, { useState, useEffect, useCallback } from "react";
import { getTokenHealth, TokenHealthResponse } from "../lib/inboxLib";
import { ReauthResult } from "../lib/authLib";
import { useSnackbar } from "../contexts/SnackbarContext";
import ReauthModal from "./ReauthModal";

interface ReauthWarningProps {
  inboxId: string;
}

const ReauthWarning: React.FC<ReauthWarningProps> = ({ inboxId }) => {
  const [tokenHealth, setTokenHealth] = useState<TokenHealthResponse | null>(
    null
  );
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { showSnackbar } = useSnackbar();

  const checkTokenHealth = useCallback(async () => {
    try {
      const health = await getTokenHealth(inboxId);
      setTokenHealth(health);
    } catch (error) {
      console.error("Failed to fetch token health:", error);
    } finally {
      setLoading(false);
    }
  }, [inboxId]);

  useEffect(() => {
    checkTokenHealth();
    const interval = setInterval(checkTokenHealth, 30000);
    return () => clearInterval(interval);
  }, [checkTokenHealth]);

  const handleSuccess = (result: ReauthResult) => {
    setShowModal(false);
    setTokenHealth(null);
    setDismissed(true);
    showSnackbar(`Gmail token refreshed for ${result.email}`, "success");
    checkTokenHealth();
  };

  if (loading || !tokenHealth?.needsReauth || dismissed) {
    return null;
  }

  const warningMessage =
    tokenHealth.reason === "insufficient_scopes"
      ? "Your account is missing required permissions. Cobutler filtering is currently paused."
      : "Your Gmail authorization is expiring soon. Re-authorize to keep filtering active.";

  const warningTitle =
    tokenHealth.reason === "insufficient_scopes"
      ? "Action Required: Missing Gmail Permissions"
      : "Action Required: Token Expiring Soon";

  return (
    <>
      <div className="bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 p-4 mb-4">
        <div className="flex items-start">
          <svg
            className="h-6 w-6 text-yellow-400 mr-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              {warningTitle}
            </h3>
            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
              {warningMessage}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="mt-2 text-sm font-medium text-yellow-800 dark:text-yellow-200 underline hover:text-yellow-900"
            >
              Fix Now →
            </button>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="ml-3 flex-shrink-0"
          >
            <svg
              className="h-5 w-5 text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <ReauthModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleSuccess}
        inboxId={inboxId}
        inboxEmail={tokenHealth.inboxEmail}
        reason={tokenHealth.reason!}
      />
    </>
  );
};

export default ReauthWarning;
